import { getRoot, getRunningActionContext } from "mobx-state-tree";
import { DEBUG_UNDO } from "../../lib/debug";
import { runningCalls } from "./tree-types";
/**
 * When added to the body of a MST action, this will prevent any changes
 * made by the action from being recorded in the CLUE undo stack. The changes
 * will still be recorded in the CLUE document history.
 *
 * If this action was called from another MST action it is considered a child
 * action. Child action changes are always recorded, so by default a warning
 * is printed when withoutUndo is called from a child action.
 *
 * There is is an option so you can record the child action changes, but not
 * record the changes when the action is the initial action:
 *
 *   `withoutUndo({ unlessChildAction: true })`
 *
 */
export function withoutUndo(options) {
    var _a;
    const actionCall = getRunningActionContext();
    if (!actionCall) {
        throw new Error("withoutUndo called outside of an MST action");
    }
    if (actionCall.parentActionEvent) {
        if (options === null || options === void 0 ? void 0 : options.unlessChildAction) {
            // The caller has explicity said it is OK to ignore the withoutUndo
            // when we are in a child action.
            return;
        }
        // It is a little weird to print all this, but it seems like a good way to leave
        // this part unimplemented.
        console.warn([
            "withoutUndo() was called by a child action. If calling a child action " +
                "with withoutUndo is something you need to do, you have a few options." +
                "- If you want to ignore the withoutUndo when in a child action:" +
                "  withoutUndo({ unlessChildAction: true })" +
                "  Note: this may cause issues if other code is always assuming this action" +
                "  will never be included in the undo history" +
                "- If you want to do something else you'll need to update this code." +
                "  Here are a couple options you might want:" +
                "    1. Apply the withoutUndo to the parent action",
            "    2. Apply the withoutUndo just to the child action",
            "  Notes:",
            "   - option 1 might have unintended consequences. Actions can become child actions" +
                "     in strange ways. For example a model action can trigger a re-render of a component." +
                "     If the component has a componentDidUpdate which calls another action, this second" +
                "     action will be considered a child action of the initial model action." +
                "   - option 2 will require changing the undo stack so it can record different " +
                "     entries from the history stack. It will also require changing the recordPatches " +
                "     function to somehow track this child action information."
        ].join('\n'));
        return;
    }
    const call = runningCalls.get(actionCall);
    if (!call) {
        // It is normal for there to be no running calls. This can happen in two cases:
        //   - the document isn't being edited so the tree monitor is disabled
        //   - the document content is part of the authored unit. In this case there is no
        //     DocumentModel so there is no middleware.
        if (DEBUG_UNDO) {
            try {
                const { context } = actionCall;
                const root = getRoot(context);
                // Use duck typing to figure out if the root is a tree
                // and its tree monitor is enabled
                if ((_a = root.treeMonitor) === null || _a === void 0 ? void 0 : _a.enabled) {
                    console.warn("cannot find action tracking middleware call");
                }
            }
            catch (error) {
                console.warn("cannot find action tracking middleware call, " +
                    "error thrown while trying to find the tree", error);
            }
        }
        return;
    }
    if (!call.env) {
        throw new Error("environment is not setup on action tracking middleware call");
    }
    call.env.undoable = false;
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoid2l0aG91dC11bmRvLmpzIiwic291cmNlUm9vdCI6IiIsInNvdXJjZXMiOlsiLi4vLi4vLi4vLi4vc3JjL21vZGVscy9oaXN0b3J5L3dpdGhvdXQtdW5kby50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFBQSxPQUFPLEVBQUUsT0FBTyxFQUFFLHVCQUF1QixFQUFFLE1BQU0saUJBQWlCLENBQUM7QUFDbkUsT0FBTyxFQUFFLFVBQVUsRUFBRSxNQUFNLGlCQUFpQixDQUFDO0FBQzdDLE9BQU8sRUFBRSxZQUFZLEVBQUUsTUFBTSxjQUFjLENBQUM7QUFFNUM7Ozs7Ozs7Ozs7Ozs7O0dBY0c7QUFDSCxNQUFNLFVBQVUsV0FBVyxDQUFDLE9BQXlDOztJQUNuRSxNQUFNLFVBQVUsR0FBRyx1QkFBdUIsRUFBRSxDQUFDO0lBQzdDLElBQUksQ0FBQyxVQUFVLEVBQUUsQ0FBQztRQUNoQixNQUFNLElBQUksS0FBSyxDQUFDLDZDQUE2QyxDQUFDLENBQUM7SUFDakUsQ0FBQztJQUVELElBQUksVUFBVSxDQUFDLGlCQUFpQixFQUFFLENBQUM7UUFDakMsSUFBSSxPQUFPLGFBQVAsT0FBTyx1QkFBUCxPQUFPLENBQUUsaUJBQWlCLEVBQUUsQ0FBQztZQUMvQixtRUFBbUU7WUFDbkUsaUNBQWlDO1lBQ2pDLE9BQU87UUFDVCxDQUFDO1FBQ0QsZ0ZBQWdGO1FBQ2hGLDJCQUEyQjtRQUMzQixPQUFPLENBQUMsSUFBSSxDQUFDO1lBQ1gsd0VBQXdFO2dCQUN4RSx1RUFBdUU7Z0JBQ3ZFLGlFQUFpRTtnQkFDakUsNENBQTRDO2dCQUM1Qyw0RUFBNEU7Z0JBQzVFLDhDQUE4QztnQkFDOUMscUVBQXFFO2dCQUNyRSw2Q0FBNkM7Z0JBQzdDLG1EQUFtRDtZQUNuRCx1REFBdUQ7WUFDdkQsVUFBVTtZQUNWLG9GQUFvRjtnQkFDcEYsMEZBQTBGO2dCQUMxRix3RkFBd0Y7Z0JBQ3hGLDRFQUE0RTtnQkFDNUUsZ0ZBQWdGO2dCQUNoRix1RkFBdUY7Z0JBQ3ZGLCtEQUErRDtTQUNoRSxDQUFDLElBQUksQ0FBQyxJQUFJLENBQUMsQ0FBQyxDQUFDO1FBQ2QsT0FBTztJQUNULENBQUM7SUFFRCxNQUFNLElBQUksR0FBRyxZQUFZLENBQUMsR0FBRyxDQUFDLFVBQVUsQ0FBQyxDQUFDO0lBQzFDLElBQUksQ0FBQyxJQUFJLEVBQUMsQ0FBQztRQUNULCtFQUErRTtRQUMvRSxzRUFBc0U7UUFDdEUsa0ZBQWtGO1FBQ2xGLCtDQUErQztRQUMvQyxJQUFJLFVBQVUsRUFBRSxDQUFDO1lBQ2YsSUFBSSxDQUFDO2dCQUNILE1BQU0sRUFBQyxPQUFPLEVBQUMsR0FBRyxVQUFVLENBQUM7Z0JBQzdCLE1BQU0sSUFBSSxHQUFHLE9BQU8sQ0FBQyxPQUFPLENBQUMsQ0FBQztnQkFDOUIsc0RBQXNEO2dCQUN0RCxrQ0FBa0M7Z0JBQ2xDLElBQUksTUFBQyxJQUFZLENBQUMsV0FBVywwQ0FBRSxPQUFPLEVBQUUsQ0FBQztvQkFDdkMsT0FBTyxDQUFDLElBQUksQ0FBQyw2Q0FBNkMsQ0FBQyxDQUFDO2dCQUM5RCxDQUFDO1lBQ0gsQ0FBQztZQUFDLE9BQVEsS0FBSyxFQUFHLENBQUM7Z0JBQ2pCLE9BQU8sQ0FBQyxJQUFJLENBQUMsK0NBQStDO29CQUMxRCw0Q0FBNEMsRUFBRSxLQUFLLENBQUMsQ0FBQztZQUN6RCxDQUFDO1FBQ0gsQ0FBQztRQUNELE9BQU87SUFDVCxDQUFDO0lBRUQsSUFBSSxDQUFDLElBQUksQ0FBQyxHQUFHLEVBQUUsQ0FBQztRQUNkLE1BQU0sSUFBSSxLQUFLLENBQUMsNkRBQTZELENBQUMsQ0FBQztJQUNqRixDQUFDO0lBQ0QsSUFBSSxDQUFDLEdBQUcsQ0FBQyxRQUFRLEdBQUcsS0FBSyxDQUFDO0FBQzVCLENBQUMifQ==